import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useId, useState, type FormEvent } from "react";
import { Navigate, useParams } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  api,
  type CatalogueItem,
  type MachineSubscriptionEvent,
  type Subscriber,
  type Subscription,
  type SubscriptionAction,
} from "@/lib/api";

function formatDateTime(value: string | null): string {
  if (!value) return "—";
  return new Date(value).toLocaleString();
}

function formatDate(value: string | null): string {
  if (!value) return "no end date";
  return new Date(value).toLocaleDateString();
}

function formatEndedAt(value: string | null): string {
  if (!value) return "current";
  return new Date(value).toLocaleString();
}

function todayDateInputValue(): string {
  return new Date().toISOString().slice(0, 10);
}

function dateInputToEndOfDayUtc(value: string): string {
  return `${value}T23:59:59.999Z`;
}

function statusVariant(status: Subscriber["status"]): "default" | "secondary" | "destructive" {
  if (status === "active") return "default";
  if (status === "cancelled" || status === "revoked") return "destructive";
  return "secondary";
}

function formatBytes(value: number): string {
  if (value < 1024) return `${value} B`;
  if (value < 1024 * 1024) return `${(value / 1024).toFixed(1)} KB`;
  return `${(value / (1024 * 1024)).toFixed(1)} MB`;
}

function formatStateValue(value: unknown): string {
  if (value === null || value === undefined) return "—";
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  return JSON.stringify(value);
}

function serviceActionLabel(action: string): string {
  return action
    .split(".")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).replaceAll("_", " "))
    .join(" ");
}

function EventTrailDialog({
  subscriber,
  subscription,
}: {
  subscriber: Subscriber;
  subscription: Subscription;
}) {
  const [open, setOpen] = useState(false);
  const { data: events, isLoading } = useQuery({
    queryKey: ["subscription-events", subscriber.id],
    queryFn: () =>
      api.listMachineSubscriptionEvents(
        subscriber.machine_id,
        subscription.id,
        subscriber.id,
      ),
    enabled: open,
  });

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="link" className="h-auto p-0 text-xs">
          Trail
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{subscriber.machine_name} trail</DialogTitle>
          <DialogDescription>Subscription-period events for this service.</DialogDescription>
        </DialogHeader>
        <div className="max-h-96 overflow-auto">
          {isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}
          {events?.length === 0 && (
            <p className="text-sm text-muted-foreground">No events recorded yet.</p>
          )}
          {events && events.length > 0 && (
            <div className="space-y-3">
              {events.map((event: MachineSubscriptionEvent) => (
                <div key={event.id} className="border-b pb-2 text-sm">
                  <div className="flex items-center justify-between gap-3">
                    <span className="font-medium">{event.event_type}</span>
                    <span className="text-xs text-muted-foreground">
                      {formatDateTime(event.occurred_at)}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground">{event.actor_type}</p>
                  {Object.keys(event.detail).length > 0 && (
                    <pre className="mt-1 whitespace-pre-wrap text-xs text-muted-foreground">
                      {JSON.stringify(event.detail, null, 2)}
                    </pre>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function CataloguePanel() {
  const queryClient = useQueryClient();
  const [name, setName] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);

  const { data: items, isLoading } = useQuery({
    queryKey: ["catalogue-items"],
    queryFn: api.listCatalogueItems,
  });

  const upload = useMutation({
    mutationFn: () => api.uploadCatalogueItem(name.trim(), file!),
    onSuccess: () => {
      setName("");
      setFile(null);
      setError(null);
      void queryClient.invalidateQueries({ queryKey: ["catalogue-items"] });
    },
    onError: (err) => setError(err instanceof Error ? err.message : "Upload failed"),
  });

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (!name.trim() || !file) return;
    upload.mutate();
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Catalogue items</CardTitle>
      </CardHeader>
      <CardContent className="space-y-5">
        <form onSubmit={handleSubmit} className="grid gap-3 md:grid-cols-[1fr_1fr_auto] md:items-end">
          <div className="space-y-2">
            <Label htmlFor="catalogue-item-name">Name</Label>
            <Input
              id="catalogue-item-name"
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder="Winter catalogue"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="catalogue-item-file">File</Label>
            <Input
              id="catalogue-item-file"
              type="file"
              onChange={(event) => setFile(event.target.files?.[0] ?? null)}
            />
          </div>
          <Button type="submit" disabled={!name.trim() || !file || upload.isPending}>
            {upload.isPending ? "Uploading…" : "Upload item"}
          </Button>
        </form>
        {error && <p className="text-sm text-destructive">{error}</p>}

        <div>
          {isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}
          {items?.length === 0 && (
            <p className="text-sm text-muted-foreground">No catalogue items uploaded yet.</p>
          )}
          {items && items.length > 0 && (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>File</TableHead>
                  <TableHead>Size</TableHead>
                  <TableHead>Uploaded</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((item: CatalogueItem) => (
                  <TableRow key={item.id}>
                    <TableCell className="font-medium">{item.name}</TableCell>
                    <TableCell className="text-muted-foreground">{item.filename}</TableCell>
                    <TableCell className="text-muted-foreground">
                      {formatBytes(item.size_bytes)}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {formatDateTime(item.created_at)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function SubscriptionDateActionDialog({
  label,
  title,
  description,
  pending,
  onSubmit,
  triggerClassName,
}: {
  label: string;
  title: string;
  description: string;
  pending: boolean;
  onSubmit: (expiresAt: string) => void;
  triggerClassName?: string;
}) {
  const inputId = useId();
  const [open, setOpen] = useState(false);
  const [expiresAt, setExpiresAt] = useState("");

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (!expiresAt) return;
    onSubmit(dateInputToEndOfDayUtc(expiresAt));
    setOpen(false);
    setExpiresAt("");
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant="link"
          className={triggerClassName ?? "h-auto p-0 text-xs"}
          disabled={pending}
        >
          {label}
        </Button>
      </DialogTrigger>
      <DialogContent>
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
            <DialogDescription>{description}</DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-4">
            <Label htmlFor={inputId}>End date</Label>
            <Input
              id={inputId}
              type="date"
              min={todayDateInputValue()}
              value={expiresAt}
              onChange={(event) => setExpiresAt(event.target.value)}
              required
            />
          </div>
          <DialogFooter>
            <Button type="submit" disabled={!expiresAt || pending}>
              {label}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function VpnDomainDialog({ subscriber }: { subscriber: Subscriber }) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [prefix, setPrefix] = useState("");
  const [error, setError] = useState<string | null>(null);

  const { data: domain } = useQuery({
    queryKey: ["vpn-domain", subscriber.machine_id],
    queryFn: () => api.getVpnDomain(subscriber.machine_id),
  });

  const invalidate = () =>
    void queryClient.invalidateQueries({ queryKey: ["vpn-domain", subscriber.machine_id] });

  const setDomain = useMutation({
    mutationFn: () => api.setVpnDomain(subscriber.machine_id, prefix.trim()),
    onSuccess: () => {
      setError(null);
      setOpen(false);
      setPrefix("");
      invalidate();
    },
    onError: (err) => setError(err instanceof Error ? err.message : "Could not set domain"),
  });

  const clearDomain = useMutation({
    mutationFn: () => api.clearVpnDomain(subscriber.machine_id),
    onSuccess: invalidate,
  });

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (!prefix.trim()) return;
    setDomain.mutate();
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (next) {
          setPrefix(domain?.prefix ?? "");
          setError(null);
        }
      }}
    >
      <DialogTrigger asChild>
        <Button variant="link" className="h-auto p-0 text-xs">
          {domain?.domain
            ? `${domain.domain}${domain.applied ? "" : " (pending)"}`
            : "Set domain"}
        </Button>
      </DialogTrigger>
      <DialogContent>
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>{subscriber.machine_name} domain</DialogTitle>
            <DialogDescription>
              Publicly exposes the box's web UI at &lt;prefix&gt;.box.denkbox.eu via Netbird's
              reverse proxy. Can be set before the box connects — it's applied automatically
              once it joins Netbird. Leave unset and the box will pick a random name itself.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-4">
            <Label htmlFor="vpn-domain-prefix">Prefix</Label>
            <Input
              id="vpn-domain-prefix"
              value={prefix}
              onChange={(event) => setPrefix(event.target.value)}
              placeholder="e.g. warehouse-3"
              required
            />
            {domain?.domain && (
              <p className="text-xs text-muted-foreground">
                Current: {domain.domain} {domain.applied ? "" : "— not yet connected to Netbird"}
              </p>
            )}
            {error && <p className="text-xs text-destructive">{error}</p>}
          </div>
          <DialogFooter className="gap-2 sm:justify-between">
            {domain?.prefix && (
              <Button
                type="button"
                variant="outline"
                disabled={clearDomain.isPending}
                onClick={() => {
                  clearDomain.mutate();
                  setOpen(false);
                }}
              >
                Clear
              </Button>
            )}
            <Button type="submit" disabled={!prefix.trim() || setDomain.isPending}>
              Save
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function SubscribeBoxDialog({ subscription }: { subscription: Subscription }) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [machineId, setMachineId] = useState<string>("");
  const [expiresAt, setExpiresAt] = useState<string>("");

  const { data: machines } = useQuery({ queryKey: ["machines"], queryFn: api.listMachines });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ["subscribers", subscription.id] });
    void queryClient.invalidateQueries({ queryKey: ["subscriptions"] });
  };

  const subscribe = useMutation({
    mutationFn: () =>
      api.subscribeMachine(machineId, subscription.id, dateInputToEndOfDayUtc(expiresAt)),
    onSuccess: () => {
      setOpen(false);
      setMachineId("");
      setExpiresAt("");
      invalidate();
    },
  });

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (!machineId || !expiresAt) return;
    subscribe.mutate();
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button>Subscribe a box</Button>
      </DialogTrigger>
      <DialogContent>
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Subscribe a box to {subscription.name}</DialogTitle>
            <DialogDescription>
              Grants the machine access to this service until the selected end date.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="subscribe-machine">Machine</Label>
              <Select value={machineId} onValueChange={setMachineId}>
                <SelectTrigger id="subscribe-machine" className="w-full">
                  <SelectValue placeholder="Select a machine" />
                </SelectTrigger>
                <SelectContent>
                  {machines?.map((machine) => (
                    <SelectItem key={machine.id} value={machine.id}>
                      {machine.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="subscribe-expires">End date</Label>
              <Input
                id="subscribe-expires"
                type="date"
                min={todayDateInputValue()}
                value={expiresAt}
                onChange={(e) => setExpiresAt(e.target.value)}
                required
              />
            </div>
          </div>
          <DialogFooter>
            <Button type="submit" disabled={!machineId || !expiresAt || subscribe.isPending}>
              Subscribe
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function ServiceDetail() {
  const { slug } = useParams<{ slug: string }>();
  const queryClient = useQueryClient();

  const { data: subscriptions, isLoading: loadingSubscriptions } = useQuery({
    queryKey: ["subscriptions"],
    queryFn: api.listSubscriptions,
  });
  const subscription = subscriptions?.find((s) => s.slug === slug);

  const { data: subscribers, isLoading: loadingSubscribers } = useQuery({
    queryKey: ["subscribers", subscription?.id],
    queryFn: () => api.listSubscribers(subscription!.id),
    enabled: !!subscription,
  });

  const runAction = useMutation({
    mutationFn: ({
      machineId,
      action,
      expiresAt,
    }: {
      machineId: string;
      action: SubscriptionAction;
      expiresAt?: string | null;
    }) => api.runSubscriptionAction(machineId, subscription!.id, action, expiresAt ?? null),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["subscribers", subscription?.id] });
      void queryClient.invalidateQueries({ queryKey: ["subscriptions"] });
    },
  });

  const runServiceAction = useMutation({
    mutationFn: ({
      subscriber,
      actionName,
    }: {
      subscriber: Subscriber;
      actionName: string;
    }) =>
      api.runSubscriptionServiceAction(
        subscriber.machine_id,
        subscription!.id,
        subscriber.id,
        actionName,
      ),
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({ queryKey: ["subscribers", subscription?.id] });
      void queryClient.invalidateQueries({ queryKey: ["subscriptions"] });
      void queryClient.invalidateQueries({
        queryKey: ["subscription-events", variables.subscriber.id],
      });
    },
  });

  if (!loadingSubscriptions && !subscription) {
    return <Navigate to="/services" replace />;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-lg font-semibold">{subscription?.name ?? "…"}</h1>
          <p className="text-sm text-muted-foreground">
            {subscription?.subscriber_count ?? 0} box
            {subscription?.subscriber_count === 1 ? "" : "es"} subscribed · last updated{" "}
            {subscription?.last_updated
              ? new Date(subscription.last_updated).toLocaleString()
              : "never"}
          </p>
        </div>
        {subscription && <SubscribeBoxDialog subscription={subscription} />}
      </div>

      {subscription?.slug === "catalogue" && <CataloguePanel />}

      <Card>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Machine</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Since</TableHead>
                <TableHead>Ends</TableHead>
                <TableHead>Closed</TableHead>
                <TableHead>Service state</TableHead>
                <TableHead>Last updated</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {loadingSubscribers && (
                <TableRow>
                  <TableCell colSpan={8} className="text-center text-muted-foreground">
                    Loading…
                  </TableCell>
                </TableRow>
              )}
              {subscribers?.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} className="text-center text-muted-foreground">
                    No boxes subscribed yet.
                  </TableCell>
                </TableRow>
              )}
              {subscribers?.map((subscriber) => (
                <TableRow key={subscriber.id}>
                  <TableCell className="font-medium">{subscriber.machine_name}</TableCell>
                  <TableCell>
                    <Badge variant={statusVariant(subscriber.status)}>{subscriber.status}</Badge>
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {formatDateTime(subscriber.started_at)}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {formatDate(subscriber.expires_at)}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {formatEndedAt(subscriber.ended_at)}
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {Object.keys(subscriber.service_state).length === 0 ? (
                      "—"
                    ) : (
                      <div className="space-y-1">
                        {Object.entries(subscriber.service_state).map(([key, value]) => (
                          <div key={key}>
                            <span className="font-medium text-foreground">{key}:</span>{" "}
                            {formatStateValue(value)}
                          </div>
                        ))}
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {formatDateTime(subscriber.updated_at)}
                  </TableCell>
                  <TableCell>
                    <div className="flex justify-end gap-2">
                      {subscription &&
                        subscriber.available_service_actions.map((actionName) => (
                          <Button
                            key={actionName}
                            variant="link"
                            className="h-auto p-0 text-xs"
                            disabled={runServiceAction.isPending}
                            onClick={() =>
                              runServiceAction.mutate({ subscriber, actionName })
                            }
                          >
                            {serviceActionLabel(actionName)}
                          </Button>
                        ))}
                      {subscription?.slug === "vpn" && <VpnDomainDialog subscriber={subscriber} />}
                      {subscription && (
                        <EventTrailDialog subscriber={subscriber} subscription={subscription} />
                      )}
                      {(subscriber.status !== "active" || subscriber.ended_at) && (
                        <SubscriptionDateActionDialog
                          label="Subscribe"
                          title={`Subscribe ${subscriber.machine_name}`}
                          description={`Choose the new end date for ${subscription?.name ?? "this service"}.`}
                          pending={runAction.isPending}
                          onSubmit={(expiresAt) =>
                            runAction.mutate({
                              machineId: subscriber.machine_id,
                              action: "subscribe",
                              expiresAt,
                            })
                          }
                        />
                      )}
                      <SubscriptionDateActionDialog
                        label="Renew"
                        title={`Renew ${subscriber.machine_name}`}
                        description={`Choose an end date after the current ${formatDate(subscriber.expires_at)} end date.`}
                        pending={runAction.isPending}
                        onSubmit={(expiresAt) =>
                          runAction.mutate({
                            machineId: subscriber.machine_id,
                            action: "renew",
                            expiresAt,
                          })
                        }
                      />
                    {subscriber.status === "active" && !subscriber.ended_at && (
                        <>
                          <Button
                            variant="link"
                            className="h-auto p-0 text-xs text-destructive"
                            disabled={runAction.isPending}
                            onClick={() =>
                              runAction.mutate({
                                machineId: subscriber.machine_id,
                                action: "cancel",
                              })
                            }
                          >
                            Cancel
                          </Button>
                          <Button
                            variant="link"
                            className="h-auto p-0 text-xs text-destructive"
                            disabled={runAction.isPending}
                            onClick={() =>
                              runAction.mutate({
                                machineId: subscriber.machine_id,
                                action: "revoke",
                              })
                            }
                          >
                            Revoke
                          </Button>
                          <Button
                            variant="link"
                            className="h-auto p-0 text-xs"
                            disabled={runAction.isPending}
                            onClick={() =>
                              runAction.mutate({
                                machineId: subscriber.machine_id,
                                action: "auto_end",
                              })
                            }
                          >
                            Auto end
                          </Button>
                        </>
                    )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
